import { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAppDispatch } from "../../../redux/hooks/useTypedSelector";
import {
  recoverPasswordAction,
  resetAuth,
} from "../../../redux/slice/auth/authSlice";
import {
  ForgetPasswordValidationSchema,
  RecoverPassword,
} from "../../../apps/auth/schema";
import { Field, FormAlert, SubmitButton } from "./fields";
import { authErrorMessage } from "./authErrorMessage";
import { AuthView } from "./AuthModalContext";
import { Icon } from "../ui";

export const ForgotPasswordForm = ({
  onSwitch,
}: {
  onSwitch: (view: AuthView) => void;
}) => {
  const dispatch = useAppDispatch();
  const [error, setError] = useState("");
  const [sentTo, setSentTo] = useState("");

  const {
    handleSubmit,
    register,
    formState: { isSubmitting, errors },
  } = useForm({
    resolver: yupResolver(ForgetPasswordValidationSchema),
    defaultValues: { email: "" },
  });

  const recover = async (data: RecoverPassword) => {
    setError("");
    const res = await dispatch(recoverPasswordAction(data));
    if (recoverPasswordAction.fulfilled.match(res)) {
      setSentTo(data.email);
    } else {
      setError(
        authErrorMessage(
          res.payload,
          "We couldn't send the reset email. Please try again.",
        ),
      );
    }
    dispatch(resetAuth());
  };

  if (sentTo) {
    return (
      <div className="flex flex-col items-center text-center gap-5 py-2">
        <div className="w-14 h-14 rounded-2xl bg-tertiary-fixed text-tertiary flex items-center justify-center">
          <Icon name="mark_email_read" className="text-3xl" />
        </div>
        <div className="flex flex-col gap-2">
          <h2 className="font-headline-md text-headline-md text-on-surface">
            Check your inbox
          </h2>
          <p className="font-body-sm text-body-sm text-on-surface-variant">
            We've sent password reset instructions to{" "}
            <strong className="text-on-surface">{sentTo}</strong>.
          </p>
        </div>
        <button
          type="button"
          onClick={() => onSwitch("signin")}
          className="w-full h-12 rounded-xl bg-surface-container hover:bg-surface-container-high text-primary font-label-lg text-label-lg transition-colors"
        >
          Back to Sign In
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <h2 className="font-headline-md text-headline-md text-on-surface">
          Reset your password
        </h2>
        <p className="font-body-sm text-body-sm text-on-surface-variant">
          Enter the email address you used to sign up and we'll send you a
          reset link.
        </p>
      </div>

      {error && <FormAlert tone="error">{error}</FormAlert>}

      <form
        className="flex flex-col gap-4"
        onSubmit={handleSubmit(recover)}
        noValidate
      >
        <Field
          id="forgot-email"
          label="Email"
          type="email"
          autoComplete="email"
          placeholder="you@example.com"
          error={errors.email?.message}
          {...register("email")}
        />
        <SubmitButton loading={isSubmitting}>Send Reset Link</SubmitButton>
      </form>

      <p className="font-body-sm text-body-sm text-on-surface-variant text-center">
        Remembered it?{" "}
        <button
          type="button"
          onClick={() => onSwitch("signin")}
          className="font-label-md text-label-md text-primary hover:underline"
        >
          Back to Sign In
        </button>
      </p>
    </div>
  );
};
